import { Injectable } from '@angular/core';

import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { once } from '../helpers/once';

import { SharedSettings } from '../models/shared-settings';

@Injectable({
    providedIn: 'root'
})
export class ActivityBlockingService {
    private _handles: BehaviorSubject<{ handle: symbol, label: string }[]>;
    private _isBusy: Observable<boolean>;

    public get IsBusy(): Observable<boolean> {
        return this._isBusy;
    }

    constructor(private settings: SharedSettings) {
        this._handles = new BehaviorSubject<{ handle: symbol, label: string }[]>([]);
        this._isBusy = this._handles.pipe(map(handles => handles.length > 0));
    }

    StartWorking(label: string = null): symbol {
        if (!this.settings.isProduction && label)
            console.log(`Activity Service: ${label} started working`);

        const handle = Symbol();
        this._handles.next([{ handle, label }, ...this._handles.value]);
        return handle;
    }

    FinishWorking(handle: symbol): void {
        if (!this.settings.isProduction) {
            const match = this._handles.value.find(h => h.handle === handle);

            if (match && match.label)
                console.log(`Activity Service: ${match.label} finished working`);
        }

        this._handles.next(this._handles.value.filter(h => h.handle !== handle));
    }

    WaitFor<T>(observable: Observable<T>, label: string = ''): Observable<T> {
        return this.Wait<T>(label)(observable);
    }

    Wait<T>(label: string = ''): (src: Observable<T>) => Observable<T> {
        return (observable: Observable<T>): Observable<T> => {
            const handle = this.StartWorking(label);

            return observable.pipe(this.Finish<T>(handle));
        };
    }

    Finish<T>(handle: symbol): (src: Observable<T>) => Observable<T> {
        return (observable: Observable<T>) => observable.pipe(
            once(_ => {
                this.FinishWorking(handle);
            }),
            catchError((err: any) => {
                this.FinishWorking(handle);
                return throwError(() => err);
            }));
    }
}
